// The mini-game cards: Blackjack, where every player at the table plays a hand on their
// phone and the winner hands out eight, and Utröstningen, where every player votes
// somebody out and the votes become raffle tickets. These are the rules; the controller
// runs the steps and the screens paint them.
//
// A mini-game lives in the synced state as `state.minigame`. Every phone answers at once,
// so answers go through the server's merge (POST /api/games/:code/answers) rather than a
// whole-state POST, and the step id they carry keeps a late answer out of the next step.
// Everything here reads that blob through normalizeMinigame first.

export const MINIGAME_KINDS = ["blackjack", "vote"];
export const MINIGAME_TITLES = {blackjack: "Blackjack", vote: "Utröstningen"};
// What the winner of Blackjack hands out, and what the raffle's loser drinks.
export const BLACKJACK_PRIZE = 8;
export const VOTE_PRIZE = 5;
// Ties play again until one hand wins — but not forever: after this many rounds the tied
// hands draw lots.
export const MAX_ROUNDS = 4;
// A seat's own stack: two dealt cards and enough hits to reach 21 on the lowest cards.
export const STACK_SIZE = 11;
// How long a finished mini-game stays up before the turn carries on.
export const DONE_MS = 6000;

const PLAY_MS = {blackjack: 30000, vote: 20000};
const CHOOSE_MS = 15000;
const KINDS = new Set(MINIGAME_KINDS);
const PHASES = new Set(["play", "choose", "done"]);
const RANKS = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];
const SUITS = ["♠", "♥", "♦", "♣"];
const RED = new Set(["♥", "♦"]);
const MAX_SEATS = 32;
const ID_LENGTH = 64;

export function blackjackDeck() {
  return SUITS.flatMap((suit) => RANKS.map((rank) => `${rank}${suit}`));
}

const CARDS = new Set(blackjackDeck());

function rankOf(code) {
  return code.slice(0, -1);
}

// Aces count eleven until that would bust the hand, then one.
export function handValue(cards) {
  let total = 0;
  let aces = 0;
  for (const code of cards) {
    const rank = rankOf(code);
    if (rank === "A") {
      aces += 1;
      total += 11;
    } else {
      total += ["J", "Q", "K"].includes(rank) ? 10 : Number(rank);
    }
  }
  while (total > 21 && aces > 0) {
    total -= 10;
    aces -= 1;
  }
  return total;
}

export function cardFace(code) {
  const suit = code.slice(-1);
  return {code, rank: rankOf(code), suit, red: RED.has(suit)};
}

// Every seat gets its own freshly shuffled deck and keeps its top STACK_SIZE cards, so a
// hit is just "one more off your own stack": nobody has to deal, and two phones hitting
// at the same moment cannot take the same card.
export function dealStacks(seats, shuffle) {
  return Object.fromEntries(seats.map((id) => [id, shuffle(blackjackDeck()).slice(0, STACK_SIZE)]));
}

// A seat's own stack, or none for a seat that was never dealt one.
export function stackOf(minigame, playerId) {
  return minigame.stacks?.[playerId] || [];
}

function handStatus(total, stood) {
  if (total > 21) return "bust";
  if (total === 21) return "twentyone";
  return stood ? "stood" : "playing";
}

// A seat's hand: once a round is decided its snapshot (`hands`), otherwise the two dealt
// cards plus every hit the seat has taken. Bust and 21 end a hand by themselves.
export function blackjackHand(minigame, playerId) {
  if (minigame.hands) {
    const cards = minigame.hands[playerId] || [];
    return {cards, total: handValue(cards), status: handStatus(handValue(cards), true)};
  }
  const stack = stackOf(minigame, playerId);
  const answer = minigame.answers?.[playerId];
  const hits = Math.min(Math.max(0, stack.length - 2), answer?.hits || 0);
  const cards = stack.slice(0, 2 + hits);
  const total = handValue(cards);
  return {cards, total, status: handStatus(total, Boolean(answer?.stand))};
}

export function blackjackStatusText({status, total}) {
  if (status === "bust") return `Tjock! ${total}`;
  if (status === "twentyone") return "21!";
  return status === "stood" ? `Står på ${total}` : `Spelar … ${total}`;
}

// The seats holding the best hand under 22. More than one is a tie, and none means
// everyone went bust — either way the round is played again.
export function blackjackLeaders(hands) {
  const totals = Object.entries(hands).map(([id, cards]) => [id, handValue(cards)]).filter(([, total]) => total <= 21);
  const best = totals.reduce((top, [, total]) => Math.max(top, total), 0);
  return {winners: totals.filter(([, total]) => total === best).map(([id]) => id), best};
}

// Who the eight go to when the winner does not choose: the biggest bust, otherwise the
// lowest count. A seat with no hand at all — one who sat the deciding round out — is the
// worst of all, because it lost a round already.
export function worstHand(ids, hands) {
  const badness = (id) => {
    if (!hands[id]) return Infinity;
    const total = handValue(hands[id]);
    return total > 21 ? 100 + total : 21 - total;
  };
  return ids.reduce((worst, id) => (worst === null || badness(id) > badness(worst) ? id : worst), null);
}

// Whether every seat still at the table has done its part in this step.
export function stepComplete(minigame, livingIds) {
  if (minigame.phase === "done") return true;
  if (minigame.phase === "choose") return typeof minigame.answers?.[minigame.winnerId]?.target === "string";
  const seats = minigame.seats.filter((id) => livingIds.includes(id));
  if (minigame.kind === "vote") return seats.every((id) => typeof minigame.answers?.[id]?.vote === "string");
  return seats.every((id) => blackjackHand(minigame, id).status !== "playing");
}

// The votes that count, most-voted first; `valid(voter, target)` is the table's rule
// for who may vote for whom.
export function voteTally(minigame, valid) {
  const counts = new Map();
  for (const [voter, answer] of Object.entries(minigame.answers || {})) {
    if (!valid(voter, answer?.vote)) continue;
    counts.set(answer.vote, (counts.get(answer.vote) || 0) + 1);
  }
  return [...counts].map(([id, votes]) => ({id, votes})).sort((first, second) => second.votes - first.votes);
}

// One raffle ticket per vote.
export function voteTickets(tally) {
  return tally.flatMap(({id, votes}) => Array.from({length: votes}, () => id));
}

// A bot draws while it is under seventeen, like a dealer.
export function botHits(stack) {
  let hits = 0;
  while (2 + hits < stack.length && handValue(stack.slice(0, 2 + hits)) < 17) hits += 1;
  return hits;
}

// A bot votes for, and hands its eight to, whoever is furthest from the end of their glass.
export function botTarget(candidates) {
  return candidates.reduce((leader, player) => (!leader || player.sipsTaken < leader.sipsTaken ? player : leader), null)?.id ?? null;
}

export function stepDuration(minigame) {
  if (minigame.phase === "done") return DONE_MS;
  return minigame.phase === "choose" ? CHOOSE_MS : PLAY_MS[minigame.kind];
}

function text(value, limit = ID_LENGTH) {
  return String(value ?? "").slice(0, limit);
}

function whole(value, min, max, fallback) {
  const number = Math.trunc(Number(value));
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
}

function entries(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? Object.entries(value).slice(0, MAX_SEATS) : [];
}

function cardMap(value) {
  return Object.fromEntries(entries(value).map(([id, cards]) => [
    text(id),
    (Array.isArray(cards) ? cards : []).filter((code) => CARDS.has(code)).slice(0, STACK_SIZE),
  ]));
}

function normalizeAnswer(answer) {
  const source = answer && typeof answer === "object" ? answer : {};
  return {
    hits: whole(source.hits, 0, STACK_SIZE - 2, 0),
    stand: source.stand === true,
    vote: typeof source.vote === "string" ? text(source.vote) : null,
    target: typeof source.target === "string" ? text(source.target) : null,
  };
}

// Each seat once, read off a bounded slice of whatever arrived.
function seatIds(value) {
  const ids = (Array.isArray(value) ? value : []).slice(0, MAX_SEATS * 4).map((id) => text(id));
  return [...new Set(ids)].slice(0, MAX_SEATS);
}

function normalizeResult(result) {
  if (!result || typeof result !== "object") return null;
  return {
    loserId: typeof result.loserId === "string" ? text(result.loserId) : null,
    amount: whole(result.amount, 0, 30, 0),
    tally: (Array.isArray(result.tally) ? result.tally : [])
      .filter((entry) => entry && typeof entry === "object")
      .slice(0, 16)
      .map((entry) => ({id: text(entry.id), votes: whole(entry.votes, 0, MAX_SEATS, 0)})),
  };
}

// The mini-game as the screens may read it: a known kind and phase, capped lists, real
// card codes only, and every answer reduced to the four fields any step asks for. A step
// with no id is no step: every answer and every countdown is keyed on it.
export function normalizeMinigame(value) {
  if (!value || typeof value !== "object" || !KINDS.has(value.kind) || !text(value.id)) return null;
  const phase = PHASES.has(value.phase) ? value.phase : "play";
  return {
    id: text(value.id),
    gameId: text(value.gameId),
    kind: value.kind,
    phase,
    round: whole(value.round, 1, MAX_ROUNDS, 1),
    // The total a replay was tied on, so the table can say why it is playing again;
    // 0 when everyone went bust, or before any replay.
    tie: whole(value.tie, 0, 21, 0),
    starterId: text(value.starterId),
    seats: seatIds(value.seats),
    stacks: value.kind === "blackjack" ? cardMap(value.stacks) : {},
    // A decided Blackjack round is read off its snapshot, so it always has one; a hand in
    // play is counted from its stack, never from a snapshot a forged blob slipped in.
    hands: value.kind === "blackjack" && phase !== "play" ? cardMap(value.hands) : null,
    answers: Object.fromEntries(entries(value.answers).map(([id, answer]) => [text(id), normalizeAnswer(answer)])),
    winnerId: typeof value.winnerId === "string" ? text(value.winnerId) : null,
    result: normalizeResult(value.result),
  };
}
